import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GetApiKeyDto } from 'services/Dtos/ApiKeyDtos';
import { GetCustomerDashboardDto } from 'services/Dtos/GetCustomerDashboardDto';

type SortField = 'id' | 'name' | 'key' | 'phone' | 'email' | 'configs';
type SortDirection = 'asc' | 'desc';

interface CustomerApiKeysTabProps {
  customer: GetCustomerDashboardDto;
  customerId: string;
  searchTerm: string;
  filteredApiKeys: GetApiKeyDto[];
  sortField: SortField;
  sortDirection: SortDirection;
  togglingGemini: number | null;
  onSearch: (term: string) => void;
  onSort: (field: SortField) => void;
  onToggleGemini: (apiKeyId: number, apiKeyName: string) => Promise<void>;
  onCreateNew: () => void;
}

const CustomerApiKeysTab: React.FC<CustomerApiKeysTabProps> = ({
  customer,
  customerId,
  searchTerm,
  filteredApiKeys,
  sortField,
  sortDirection,
  togglingGemini,
  onSearch,
  onSort,
  onToggleGemini,
  onCreateNew
}) => {
  const navigate = useNavigate();

  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <i className="bi bi-caret-up-fill ms-1"></i> :
      <i className="bi bi-caret-down-fill ms-1"></i>;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("API key copied to clipboard!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy API key", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  return (
    <div className="row mb-4">
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="input-group mb-3">
            <span className="input-group-text">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search API keys..."
              value={searchTerm}
              onChange={(e) => onSearch(e.target.value)}
            />
            {searchTerm && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => onSearch("")}
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {filteredApiKeys.length === 0 ? (
        <div className="alert alert-info">
          {customer.apiKeys.length === 0
            ? "No API keys associated with this customer."
            : "No API keys match your search criteria."}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <th
                  onClick={() => onSort('id')}
                  style={{ cursor: 'pointer' }}
                >
                  Id {renderSortIndicator('id')}
                </th>
                <th
                  onClick={() => onSort('name')}
                  style={{ cursor: 'pointer' }}
                >
                  Name {renderSortIndicator('name')}
                </th>
                <th
                  onClick={() => onSort('key')}
                  style={{ cursor: 'pointer' }}
                >
                  API Key {renderSortIndicator('key')}
                </th>
                <th
                  onClick={() => onSort('phone')}
                  style={{ cursor: 'pointer' }}
                >
                  Phone {renderSortIndicator('phone')}
                </th>
                <th
                  onClick={() => onSort('email')}
                  style={{ cursor: 'pointer' }}
                >
                  Email {renderSortIndicator('email')}
                </th>
                <th
                  onClick={() => onSort('configs')}
                  style={{ cursor: 'pointer' }}
                >
                  Configurations {renderSortIndicator('configs')}
                </th>
                <th>Gemini</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApiKeys.map((apiKey) => (
                <tr key={apiKey.id}>
                  <td>{apiKey.id}</td>
                  <td>{apiKey.name}</td>
                  <td>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={apiKey.key}
                        readOnly
                        aria-label="API Key"
                      />
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={() => copyToClipboard(apiKey.key)}
                        title="Copy to clipboard"
                      >
                        <i className="bi bi-clipboard"></i>
                      </button>
                    </div>
                  </td>
                  <td>{apiKey.phone || "-"}</td>
                  <td>{apiKey.email || "-"}</td>
                  <td>{apiKey.apiKeyConfigs.length}</td>
                  <td>
                    <button
                      className={`btn btn-sm ${apiKey.shouldUseGemini ? 'btn-success' : 'btn-outline-secondary'}`}
                      onClick={() => onToggleGemini(apiKey.id, apiKey.name)}
                      disabled={togglingGemini === apiKey.id}
                      title={apiKey.shouldUseGemini ? 'Gemini enabled' : 'Gemini disabled'}
                    >
                      {togglingGemini === apiKey.id ? (
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      ) : (
                        <i className={`bi bi-robot me-1`}></i>
                      )}
                      {apiKey.shouldUseGemini ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => navigate(`/customers/${customerId}/api-keys/${apiKey.id}/details`)}
                      title="View Details"
                    >
                      <i className="bi bi-three-dots"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Floating Create New API Key Button */}
      <button
        className="btn btn-primary rounded-circle shadow-lg"
        onClick={onCreateNew}
        style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          width: "60px",
          height: "60px",
          fontSize: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        title="Create New API Key"
      >
        <i className="bi bi-plus"></i>
      </button>
    </div>
  );
};

export default CustomerApiKeysTab;
